/**
 * FeedbackWidget — universal floating "Code Blue"-style feedback button.
 *
 * Renders bottom-right on every authenticated page (mounted in DashboardLayout).
 * Click opens a Dialog that captures: staff selector (defaults to current user),
 * module dropdown (auto-derived from sidebar routes), submodule input,
 * subject, feedback text (≤4000 chars), and optional 5-star rating.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { feedbackAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { MessageSquarePlus, Send, Star, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

// Auto-derived module list (keep in sync with Sidebar). "Other" lives at the end.
const MODULES = [
  'Dashboard', 'Patients', 'Appointments', 'Queue', 'EMR', 'Vitals', 'Prescriptions',
  'Lab', 'Imaging', 'Pharmacy', 'Insurance', 'Blood Bank', 'Mortuary', 'Canteen',
  'Store', 'IPD Admissions', 'Wards', 'Bed Status', 'Discharge Summaries',
  'Payments', 'Invoice History', 'Invoices', 'Refunds', 'Billing Config',
  'Profit & Loss', 'Revenue', 'Vendors', 'Expenses', 'Finance Config',
  'Staff Management', 'Attendance', 'Leave', 'Shifts & Roster', 'Payroll',
  'Doctor Config', 'Doctor Availability', 'KYC Compliance', 'Celebrations',
  'Public Holidays', 'My Profile', 'My Attendance', 'Notices', 'Notice Dashboard',
  'Roles & Permissions', 'Access Control', 'Security', 'Branding',
  'Hospitals', 'Subscriptions', 'Platform Health', 'Audit Logs',
  'Authentication / Login', 'Print / Documents', 'Other',
];

// Best-effort mapping from current route → module label
const routeToModule = (pathname) => {
  if (!pathname) return '';
  const p = pathname.toLowerCase();
  const map = {
    '/dashboard': 'Dashboard', '/patients': 'Patients', '/appointments': 'Appointments',
    '/queue': 'Queue', '/vitals': 'Vitals', '/prescriptions': 'Prescriptions',
    '/lab': 'Lab', '/imaging': 'Imaging', '/pharmacy': 'Pharmacy',
    '/insurance': 'Insurance', '/bloodbank': 'Blood Bank', '/mortuary': 'Mortuary',
    '/canteen': 'Canteen', '/store': 'Store', '/admissions': 'IPD Admissions',
    '/wards': 'Wards', '/bed-status': 'Bed Status', '/discharge-summaries': 'Discharge Summaries',
    '/payments': 'Payments', '/invoice-history': 'Invoice History', '/invoices': 'Invoices',
    '/refunds': 'Refunds', '/billing-config': 'Billing Config',
    '/profit-loss': 'Profit & Loss', '/revenue': 'Revenue', '/vendors': 'Vendors',
    '/expenses': 'Expenses', '/finance-config': 'Finance Config',
    '/staff': 'Staff Management', '/attendance': 'Attendance', '/leave': 'Leave',
    '/shifts': 'Shifts & Roster', '/payroll': 'Payroll',
    '/doctor-config': 'Doctor Config', '/doctor-availability': 'Doctor Availability',
    '/kyc-compliance': 'KYC Compliance', '/celebrations-settings': 'Celebrations',
    '/public-holidays': 'Public Holidays', '/my-profile': 'My Profile', '/my-attendance': 'My Attendance',
    '/notices': 'Notices', '/notice-dashboard': 'Notice Dashboard',
    '/roles': 'Roles & Permissions', '/access-control': 'Access Control',
    '/security': 'Security', '/branding': 'Branding',
    '/hospitals': 'Hospitals', '/subscriptions': 'Subscriptions',
    '/platform-health': 'Platform Health', '/audit-logs': 'Audit Logs',
    '/feedback-dashboard': 'Other',
    '/ipd-referrals': 'IPD Admissions',
  };
  // Match by longest prefix
  const keys = Object.keys(map).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (p === k || p.startsWith(k + '/')) return map[k];
  }
  if (p.startsWith('/discharge/print') || p.startsWith('/lab/print') || p.startsWith('/pharmacy/print') || p.startsWith('/invoice/print') || p.startsWith('/insurance/print') || p.startsWith('/preauth/print')) return 'Print / Documents';
  return '';
};

const FeedbackWidget = () => {
  const { user, hospital } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffId, setStaffId] = useState('');
  const [module, setModule] = useState('');
  const [submodule, setSubmodule] = useState('');
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const isAuthed = !!user;
  const isStaffWithHospital = isAuthed && !!hospital?.id;

  // Lazy load staff options + autoselect self
  useEffect(() => {
    if (!open || !isStaffWithHospital) return;
    setStaffId(user.id);
    setModule(routeToModule(location.pathname) || '');
    feedbackAPI.staffOptions()
      .then(res => setStaffOptions(res.data || []))
      .catch(() => setStaffOptions([{ id: user.id, first_name: user.first_name, last_name: user.last_name, email: user.email, role_name: user.role_name }]));
  }, [open, isStaffWithHospital, user?.id, location.pathname]);

  const selected = useMemo(() => staffOptions.find(s => s.id === staffId), [staffOptions, staffId]);

  const submit = async () => {
    if (!subject.trim()) return toast.error('Subject is required');
    if (!text.trim()) return toast.error('Feedback text is required');
    if (text.length > 4000) return toast.error('Feedback must be ≤ 4000 characters');
    setSubmitting(true);
    try {
      await feedbackAPI.create({
        staff_user_id: staffId || user.id,
        module: module || 'Other',
        submodule: submodule || null,
        subject: subject.trim(),
        feedback_text: text.trim(),
        rating: rating || null,
        client_local_time: new Date().toString(),
        page_url: window.location.pathname + window.location.search,
      });
      toast.success('Thanks! Your feedback has been recorded.');
      setOpen(false);
      // Reset for next submission
      setSubject(''); setText(''); setRating(0); setHover(0); setSubmodule('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit feedback');
    } finally { setSubmitting(false); }
  };

  // Hide on print pages and when not authed
  if (!isAuthed) return null;
  if (location.pathname.includes('/print/')) return null;

  // v37b2 — On Nurse Workstation the Code Blue / Emergency button also lives at bottom-right (h-16 w-16),
  // so stack the Feedback button above it.
  const onNurseWorkstation = location.pathname === '/nurse-dashboard' || location.pathname.startsWith('/nurse-dashboard');
  const fabPosClass = onNurseWorkstation ? 'fixed bottom-28 right-6' : 'fixed bottom-6 right-6';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`${fabPosClass} z-40 group flex items-center gap-2 pl-3 pr-4 py-3 rounded-full bg-gradient-to-br from-rose-500 to-fuchsia-600 text-white font-semibold shadow-lg shadow-rose-500/30 hover:shadow-xl hover:shadow-fuchsia-500/40 hover:scale-105 transition-all print:hidden`}
        title="Share Feedback"
        data-testid="feedback-floating-btn"
        aria-label="Share Feedback">
        <MessageSquarePlus size={18} />
        <span className="text-xs hidden sm:inline-block max-w-0 group-hover:max-w-[8rem] overflow-hidden whitespace-nowrap transition-all">
          Feedback
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-rose-500 to-fuchsia-600 flex items-center justify-center text-white">
                <MessageSquarePlus size={16} />
              </div>
              Share Feedback
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Help us improve the system. We capture your context automatically — your name, role, time, and page.</p>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="md:col-span-2">
              <Label>Staff Name *</Label>
              <Select value={staffId} onValueChange={setStaffId}>
                <SelectTrigger data-testid="feedback-staff-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  {staffOptions.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name} {s.role_name ? `· ${s.role_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selected && (
                <div className="mt-1.5 text-[10px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>📧 {selected.email || '—'}</span>
                  <span>📞 {selected.phone || '—'}</span>
                  <span>🪪 {selected.role_name || '—'}</span>
                  {selected.user_id && <span>🆔 {selected.user_id}</span>}
                </div>
              )}
            </div>

            <div>
              <Label>Module *</Label>
              <Select value={module} onValueChange={setModule}>
                <SelectTrigger data-testid="feedback-module-select"><SelectValue placeholder="Select module" /></SelectTrigger>
                <SelectContent className="max-h-60">{MODULES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Submodule / Page (optional)</Label>
              <Input value={submodule} onChange={e => setSubmodule(e.target.value)} placeholder="e.g., Lab Orders tab" data-testid="feedback-submodule" />
            </div>

            <div className="md:col-span-2">
              <Label>Subject *</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="One-line summary" data-testid="feedback-subject" maxLength={200} />
            </div>

            <div className="md:col-span-2">
              <Label>Feedback *</Label>
              <Textarea rows={5} value={text} onChange={e => setText(e.target.value.slice(0, 4000))} placeholder="What worked well? What can we improve?" data-testid="feedback-text" />
              <p className="text-[10px] text-slate-400 text-right mt-1">{text.length} / 4000</p>
            </div>

            <div className="md:col-span-2">
              <Label>Rating <span className="text-slate-400 font-normal">(optional)</span></Label>
              <div className="flex items-center gap-1 mt-1" onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map(n => {
                  const active = (hover || rating) >= n;
                  return (
                    <button key={n} type="button"
                      onClick={() => setRating(n === rating ? 0 : n)}
                      onMouseEnter={() => setHover(n)}
                      className="transition" data-testid={`feedback-star-${n}`}>
                      <Star size={28} className={active ? 'text-amber-400 fill-amber-400' : 'text-slate-300'} />
                    </button>
                  );
                })}
                {rating > 0 && (
                  <button type="button" onClick={() => { setRating(0); setHover(0); }} className="ml-2 text-[10px] text-slate-400 hover:text-slate-600 inline-flex items-center gap-1">
                    <X size={11} /> clear
                  </button>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="bg-gradient-to-br from-rose-500 to-fuchsia-600 hover:opacity-90 text-white" data-testid="feedback-submit-btn">
              <Send size={14} className="mr-1.5" /> {submitting ? 'Sending…' : 'Send Feedback'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackWidget;
